import * as React from "react";

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Container } from "@mui/material";
import numeral from "numeral";

import { nanoid } from "nanoid";
import { useLocation } from "react-router";
import { processCheckoutSession, processCheckoutTokensSession } from '../api/payment';
import { ActiveOrderModal } from "../components/active-order-modal";
import { AppLogo } from "../components/app-logo";
import { CartModal } from '../components/cart-modal';
// import { DataExplorationModule } from '../components/explore';
const DataExplorationModule = React.lazy(() => import('../components/explore'))
// render inside <Suspense fallback={<Loader />}>
import { LoginModal } from "../components/login-modal";
import { Meta } from "../components/meta";
import { NavigationContent } from '../components/navigation-content';
import { PurchaseConfirmationModal } from '../components/purchase-confirmation-modal';
import { PurchaseLocationModal } from '../components/purchase-location-modal';
import { PurchaseRequestModal } from "../components/purchase-request-modal";
import SimpleSearch from "../components/simple-search";
import StyledMapContainer from '../components/styled-map-container';
import { UserMessages } from "../components/user-messages";
import { UserNavItem } from '../components/user-nav-item';
import { AccountContext } from '../context/account';
import { MapDataContext } from '../context/map-data';
import { DataSourceModel, MapLocation } from '../core/global';
import { retrieveItem, storeItem } from "../core/storage";
import useWindow from "../hooks/useWindow";
import styles from "../styles/pages/dashboard.module.scss";

const drawerWidth: number = 280;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(7),
        },
      }),
    },
  }),
);

const defaultTheme = createTheme();

const DashboardPage: React.FC<any> = ({
  ...res
}) => {
  const [open, setOpen] = useState(false);
  const [modelToShowDescription, setModelToShowDescription] = useState<null | DataSourceModel>(null);

  const _window = useWindow();
  const location = _window.location
  
  const { pathname } = useLocation()
  
  useEffect(() => {
    if (pathname && typeof pathname === 'string' && pathname.includes('/models/')) {
      const parts = pathname.split('/models/').join('').replace(/\/$/, '').split('-')
      const [provider, model, geography, _version] = parts;
      const version = _version && _version === 'null' ? null : _version;
      const key = [provider, model, geography, `${version}`].join('-');
      setModelToShowDescription({provider, model, geography, version, key})
    }
  }, [pathname])

  const {
    cart,
    showCart, 
    setShowCart,
    showPurchaseData,
    setShowPurchaseData,
    refetchCart,
    refetchProjects,
    purchaseTokens,
    userTokensTotal,
    activeOrderId,
    setActiveOrderId,
    transaction,
    setTransaction,
    companyPurchaseRequests,
    refetchCompanyRequests,
    showApprovePurchaseModal,
    setShowApprovePurchaseModal,
    userMessages,
    showLoginModal, 
    setShowLoginModal,
  } = useContext(AccountContext);

  const {
    setMapCenter,
    updateMeshData,
  } = useContext(MapDataContext);

  const toggleDrawer = () => {
    const newState = !open
    setOpen(newState);
    const savedLastLocation: any = retrieveItem('map-location') || {}
    storeItem('map-location', {
      ...savedLastLocation,
      drawerOpen: newState
    })
  };

  const retrieveLastLocation = useCallback(() => {
    const savedLastLocation: any = retrieveItem('map-location')
    if (savedLastLocation) {
      const {
        drawerOpen,
      } = savedLastLocation || {}
      if (typeof drawerOpen === "boolean") {
        setOpen(drawerOpen)
      }
    }
  }, [])

  useEffect(() => {
    refetchCompanyRequests();
    retrieveLastLocation();
  }, [])

  const processedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!transaction) return

    const {isPaymentSuccess, type, processingToken, transactionId, tokens} = transaction
    if (!processingToken || !transactionId) return;

    // Guard: only process a given transaction_id once per mount.
    if (processedRef.current === transactionId) return;
    processedRef.current = transactionId;

  
    if (tokens) {
      // Tokens have been purchased
        // Process tokens
        const execute = async () => {
        await processCheckoutTokensSession({
          tokens: tokens ? `${tokens}` : null,
          processing_token: `${processingToken}`,
          transaction_id: `${transactionId}`
        })
        refetchCart()
        refetchProjects()
      }
      execute()
    } else {
      const execute = async () => {
        await processCheckoutSession({
          processing_token: `${processingToken}`,
          transaction_id: `${transactionId}`
        })
        refetchCart()
        refetchProjects()
      }
      // Process basket for card transactions only
      if (type && type === "card") {
        execute()
      }
    }
  }, [transaction])

  const cartCounterLabel = useMemo(() => {
    let label = ''
    if (purchaseTokens.length > 0 ) {
      label = `${purchaseTokens.length}`
    } else if (cart && Array.isArray(cart) && cart.length > 0) {
      label = `${cart.length}`
    }
    return label
  }, [cart, purchaseTokens])

  return (
    <>
      <Meta title="Dashboard for MetOceanAtlas" />
      <ThemeProvider theme={defaultTheme} >
          <AppBar 
            position="absolute" 
            // open={open} 
          >
            <Toolbar
              variant="dense"
              sx={{
                pr: '12px !important', // keep right padding when drawer closed
                backgroundColor: '#010824',
              }}
            >
              <IconButton
                id="toggle-navigation-drawer"
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer}
                sx={{
                  marginRight: '36px',
                  paddingLeft: '3px',
                  // ...(open && { display: 'none' }),
                }}
              >
                {!open ? <MenuIcon /> : <CloseIcon />}
              </IconButton>
              <AppLogo />
              {userTokensTotal > 0 && 
                <div className={styles.tokens}>
                  Tokens: {numeral(userTokensTotal).format("0,0")}
                </div>
              }
              <SimpleSearch
                onLocationChange={(loc: MapLocation, useModel: DataSourceModel | null) => {
                  const {location: {lat, lng}} = loc
                  const coords = `${lat},${lng}`
                  updateMeshData({coords, useModel})
                  setMapCenter({...loc, hash: nanoid()})
                }}
              />
              <IconButton
                id="show-basket-button"
                onClick={() => {
                  setShowCart(true)
                }}
                color="inherit" 
                sx={{ml: "12px"}}
              >
                {cartCounterLabel ?
                  <Badge badgeContent={cartCounterLabel} color="warning">
                    <ShoppingCartIcon sx={{color: '#fff'}} />
                  </Badge>
                  : <ShoppingCartIcon sx={{color: '#fff'}} />
                }
                
              </IconButton>
            </Toolbar>
          </AppBar>

        <Box sx={{ display: 'flex', height: '100vh' }}>
          <CssBaseline />
          <Drawer 
            id="navigation-drawer"
            variant="permanent" 
            open={open} 
            className={styles.drawer} 
            PaperProps={{
                style: { 
                  width: open ? "386px" : "56px"
                }
              }}
            >
            <div>
              <Toolbar
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minHeight: '56px !important',
                }}
              >
                <IconButton onClick={toggleDrawer}>
                  <ChevronLeftIcon />
                </IconButton>
              </Toolbar>
              <Divider />
              <List component="nav" className={styles.list}>
                <NavigationContent open={open} changeOpen={setOpen} openSourcesModal={modelToShowDescription !== null ? modelToShowDescription : null} />
              </List>
            </div>
            <div>
              <UserNavItem open={open} />
            </div>
          </Drawer>
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[900],
              flexGrow: 1,
              height: 'calc(100vh - 56px)',
              overflow: 'auto',
              marginTop: '56px'
            }}
          >
            <Container maxWidth={false} sx={{ mt: 0, mb: 0, }} disableGutters>
              <StyledMapContainer open={open} />
              <DataExplorationModule menuIsOpen={open} />
            </Container>
          </Box>
        </Box>
      </ThemeProvider>
      {showCart && <CartModal onClose={() => {
        setShowCart(false)
      }} />}
      {showPurchaseData &&
        <PurchaseLocationModal onClose={() => {
          setShowPurchaseData(false)
        }} />
      }
      {activeOrderId &&
        <ActiveOrderModal 
          orderId={activeOrderId}
          onClose={() => {
            setActiveOrderId(null)
          }}
        />
      }
      {transaction && 
        <PurchaseConfirmationModal
          tokens={transaction?.tokens ? `${transaction?.tokens}` : null}
          success={transaction?.isPaymentSuccess}
          isPurchaseRequest={transaction?.isPurchaseRequest}
          onClose={() => {
            if (transaction?.isRedirect) {
              location.href = '/dashboard'
            } else {
              setTransaction(null)
            }
            
          }} />
      }
      {showApprovePurchaseModal &&
        <PurchaseRequestModal
          onClose={() => {
            setShowApprovePurchaseModal(null)
          }}
        />
      }
      <UserMessages />
      <LoginModal open={showLoginModal} handleClose={() => {
        setShowLoginModal(false)
      }} />
    </>
  );
}

export default DashboardPage

export const Head: any = () => <title>Dashboard</title>
